import { Close, List as IconList, Summarize } from '@mui/icons-material';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Divider,
  Drawer,
  Fade,
  Grid,
  Link,
  Radio,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ImageModal, Loader, Modal, Page, PageTitleWithBack } from '../../../components';
import { AlertModal } from '../../../components/alert';
import { UserContext } from '../../../context';
import { GlobalContext } from '../../../context/global';
import {
  doesContractExist,
  getBrickJointOptions,
  getBuildById,
  getBuildOptionCategory,
  getShutterColorOptions,
  getSidingColorOptions,
  getStoneColorOptions,
  updateBuild
} from '../../../fetch';
import {
  formatMoney,
  groupByKey,
  isHomeowner,
  scrollToTop,
  scrollToTopSticky
} from '../../../helpers';
import { ICategory, IOptionImage, ISelectedSpecOption, ISpec, ISpecUpdate } from '../../../models';
import { BuildSummary } from './BuildSummary';
import { AccordionListOptions, ListOptions } from './ListOptions';
import { SelectionDetails } from './selection-details/SelectionDetails';
import { SpecColorSchemeOptions } from './SpecColorSchemeOptions';
import { StickyBoxFeatureFlag } from './StickyBoxFeatureFlag';
import { useFlags } from '../../../hooks';

interface ISpecBuildOptions {
  path?: string;
  shouldShowFinishSpecButton?: boolean;
  pageTitle?: string;
}

const ContractOptionsFinishedAlert: { message: string; severity: AlertColor } = {
  message: 'All Contract Options are complete, please generate the Contract Documents.',
  severity: 'success'
};

enum BuildStatus {
  Approved = 'BuildApproved'
}

export const SpecBuildOptions: FC<ISpecBuildOptions> = ({
  path,
  shouldShowFinishSpecButton = true,
  pageTitle = 'Spec Build Options'
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get('redirect');
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery('(max-width: 1260px)');
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{ userHasOneBuild: boolean }>();
  const fullPath = location.pathname;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);

  const {
    lotPremiumOptionDepositCalculation: lotPremiumOptionDepositCalculationFF,
    selectsPageSlidingMenus: selectsPageSlidingMenusFF
  } = useFlags();

  const [isTestBuild, setIsTestBuild] = useState<boolean>(false);
  const [isShowingSelectionDetails, showSelectionDetails] = useState(false);
  const [swatchImages, showSwatchModal] = useState<IOptionImage[] | null>(null);
  /**
   * State fetched from the /api/specs/:id endpoint.
   */
  const [buildClientId, setBuildClientId] = useState<number>();
  const [selectedOptions, setSelectedOptions] = useState<ISelectedSpecOption[]>([]);
  const [tenDayCategories, setTenDayCategories] = useState<string[]>([]);
  const [contractCategories, setContractCategories] = useState<string[]>([]);
  const [spec, setSpec] = useState<ISpec | null>(null);
  const [missingExteriorColorScheme, setMissingExteriorColorScheme] = useState<boolean>();
  const [isFetching, setIsFetching] = useState(false);
  const [initialExteriorColorScheme, setInitialExteriorColorScheme] = useState<
    ISelectedSpecOption[]
  >([]);
  /**
   * Controls which category is selected in the left pane as well as the
   * Previous and Next buttons.
   */
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number | null>(null);
  /**
   * Controls for left and right panes on mobile widths.
   */
  const [isCategoriesDrawerOpen, setCategoriesDrawerOpen] = useState(false);
  const [isBuildSummaryDrawerOpen, setBuildSummaryDrawerOpen] = useState(false);
  /**
   * The currently selected category.
   */
  const [category, setCategory] = useState<ICategory | undefined>(undefined);
  const [isFetchingCategory, setIsFetchingCategory] = useState(false);
  /**
   * The imageURL of the build option the user clicked to view a larger image of.
   */
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  /**
   * The current title of the center content, based off the selected category.
   */
  const title: string = useMemo(
    () =>
      contractCategories[selectedCategoryIndex] ??
      tenDayCategories[selectedCategoryIndex - contractCategories.length] ??
      '',
    [contractCategories, selectedCategoryIndex, tenDayCategories]
  );
  /**
   * Is the selected category a ten day category?
   */
  const isTenDayCategorySelected = selectedCategoryIndex >= contractCategories.length;

  const contractSummary = useMemo(() => {
    return `${spec?.contractUpgradeCount} Upgrades: ${formatMoney(spec?.contractUpgradeAmount)}`;
  }, [spec?.contractUpgradeCount, spec?.contractUpgradeAmount]);

  const tenDaySummary = useMemo(() => {
    return `${spec?.tenDayUpgradeCount} Upgrades: ${formatMoney(spec?.tenDayUpgradeAmount)}`;
  }, [spec?.tenDayUpgradeCount, spec?.tenDayUpgradeAmount]);

  /**
   * The House Contingency Information
   */
  const [hasHouseSaleContingency, setHasHouseSaleContingency] = useState<boolean>(false);
  const [houseSaleContingencyLocation, setHouseSaleContingencyLocation] = useState<string>('');

  /**
   * Fetch and load data for this page.
   */
  useEffect(() => {
    fetchDetails(true, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotPremiumOptionDepositCalculationFF]);

  useEffect(() => {
    const missingScheme = spec?.missingTenDayCategories?.some(
      category => category?.category === 'Exterior Color Scheme'
    );
    setMissingExteriorColorScheme(missingScheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const hasOneBuild = location?.state?.userHasOneBuild;
  const getBackUrl = () => {
    if (hasOneBuild) {
      return '/';
    } else if (isHomeowner(user)) {
      return '/my-build';
    } else if (!isHomeowner(user)) {
      return !fullPath.includes('build-options')
        ? `${path ?? '/development'}/specs`
        : `/sales/contracting/purchasers/${buildClientId}/builds`;
    }
  };

  const getBackText = () => {
    if (hasOneBuild) {
      return 'Back to Home';
    } else {
      return isHomeowner(user) || fullPath.includes('build-options')
        ? 'Back to Builds'
        : 'Back to Manage Specs';
    }
  };

  // Used to show the Alert Modal. It will only call the endpoint once, and only after the page has loaded
  const [isPageLoading, setIsPageLoading] = useState(true);
  const fetchDetails = async (
    showLoader?: boolean,
    callOneOffSave?: boolean,
    dontCallCategory?: boolean,
    showCategoryLoader?: boolean
  ) => {
    setIsFetchingDetails(true);
    if (showLoader) {
      setIsFetching(true);
    }

    try {
      const res = await getBuildById(id);

      //verify buildStatus has a value before setting isTestBuild
      if (res.buildStatus) {
        setIsTestBuild(res.buildStatus === 'Test');
      }

      setBuildClientId(res.clientId);
      setTenDayCategories(res.tenDayCategories);
      setContractCategories(res.contractCategories);
      setSelectedOptions(
        res.selectedOptions.map(option => ({
          ...option,
          // used to track the multiple siding colors for color scheme options
          // so we can only select one option per group, "Lap", "Board and Batten", "Shake"
          optionType: option?.optionId?.split?.('_')?.[1] ?? ''
        }))
      );
      setInitialExteriorColorScheme(
        res.selectedOptions.filter(option => option.category === 'Exterior Color Scheme')
      );
      setSpec(res);
      setHasHouseSaleContingency(res.hasHouseSaleContingency);
      setHouseSaleContingencyLocation(res.houseSaleContingencyLocation ?? '');
      const categoryRes = res.contractCategories[selectedCategoryIndex];
      if (callOneOffSave) {
        await saveOneOffSpec({
          selectedOptions: res.selectedOptions
        });
      }
      if (!dontCallCategory) {
        await fetchCategory(categoryRes, showCategoryLoader);
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        history.push('/');
        return;
      }

      if (isHomeowner(user)) {
        return;
      }
      history.push(getBackUrl());
    } finally {
      setIsFetching(false);
      setIsFetchingDetails(false);
      setIsPageLoading(false);
    }
  };

  // Because the initial fetchDetails call is made every time we save, I need to keep track of the first update
  // so that I can avoid setting the selectedCategoryIndex to 0 every time we save
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (spec && firstUpdate.current && !isFetchingDetails) {
      const firstMissingCategory = spec.missingTenDayCategories?.[0]?.category;
      const firstTenDayOptionWithoutSelection = spec?.tenDayCategories?.findIndex(cat => {
        return cat === firstMissingCategory;
      });

      const firstTenDayOptionWithoutSelectionIndex =
        firstTenDayOptionWithoutSelection < 0 ? 0 : firstTenDayOptionWithoutSelection;

      const firstTenDayOption = spec.contractCategories.length;

      const shouldShowTenDayOption =
        spec?.missingTenDayCategories !== null && spec?.missingContractCategories === null;

      setSelectedCategoryIndex(
        shouldShowTenDayOption ? firstTenDayOptionWithoutSelectionIndex + firstTenDayOption : 0
      );
      firstUpdate.current = false;
      return;
    }
  }, [spec, isFetchingDetails]);

  const fetchCategory = async (
    initialCategory?: string,
    showLoader: boolean = true,
    isCancelled: boolean = false
  ) => {
    showLoader && setIsFetchingCategory(true);

    try {
      const categories = contractCategories.concat(tenDayCategories);
      const selectedCategoryName = initialCategory || categories[selectedCategoryIndex];
      const isTenDay = initialCategory ? false : selectedCategoryIndex >= contractCategories.length;
      if (!selectedCategoryName) return;
      const res = await getBuildOptionCategory(id, {
        category: selectedCategoryName,
        isTenDayCategory: isTenDay
      });
      if (res.name === 'Color Scheme Options') {
        // If Exterior Color Scheme has changed, go get latest options for various endpoints
        const selectedExteriorColorScheme = selectedOptions.filter(
          option => option.category === 'Exterior Color Scheme'
        );
        const hasExteriorColorSchemeUpdated = !deepEqual(
          initialExteriorColorScheme,
          selectedExteriorColorScheme
        );
        if (hasExteriorColorSchemeUpdated) {
          let updatedCategory = res;
          updatedCategory = await fetchBrickJointOptions(updatedCategory);
          updatedCategory = await fetchStoneColors(updatedCategory);
          updatedCategory = await fetchShutterColors(updatedCategory);
          updatedCategory = await fetchSidingColors(updatedCategory);
          if (!isCancelled) {
            setCategory(updatedCategory);
          }
        } else {
          if (!isCancelled) {
            setCategory(res);
          }
        }
      } else {
        if (!isCancelled) {
          setCategory(res);
        }
      }
    } catch (_) {
    } finally {
      if (!isCancelled) {
        setIsFetchingCategory(false);
      }
    }
  };

  const contactCategoriesStoneTypeSelection =
    selectedOptions?.find(option => option.subCategory === 'Stone Type')?.optionId ?? null;

  const [stoneColorLoading, setStoneColorLoading] = useState(false);

  const handleStoneColor = async () => {
    setStoneColorLoading(true);
    await fetchDetails(false, false, false, true);
    setStoneColorLoading(false);
  };

  const contactCategoriesMainAreaFlooringSelection =
    selectedOptions?.find(
      option => option.subCategory === 'Hardwood' || option.subCategory === 'Main Area Flooring'
    )?.optionId ?? null;

  const [mainAreaFlooringLoading, setMainAreaFlooringLoading] = useState(false);

  const handleMainAreaFlooring = async () => {
    setMainAreaFlooringLoading(true);
    await fetchDetails(false, false, false, true);
    setMainAreaFlooringLoading(false);
  };
  useEffect(() => {
    if (contactCategoriesStoneTypeSelection) {
      handleStoneColor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactCategoriesStoneTypeSelection]);

  useEffect(() => {
    if (contactCategoriesMainAreaFlooringSelection) {
      handleMainAreaFlooring();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactCategoriesMainAreaFlooringSelection]);

  const fetchBrickJointOptions = async (cat: ICategory) => {
    const exteriorColorSchemeOptions: ISelectedSpecOption[] = selectedOptions.filter(
      option => option.category === 'Exterior Color Scheme'
    );
    const exteriorColorSchemeCode = exteriorColorSchemeOptions.filter(
      option => option.subCategory === 'Exterior Color Scheme'
    )[0]?.name;
    try {
      const res = await getBrickJointOptions(spec.buildId, {
        exteriorColorSchemeCode
      });
      // If Brick Joint options, update/add to subCategories based on new fetch
      if (res.options.length) {
        const copy = JSON.parse(JSON.stringify(cat));
        const copyIndex = copy.subCategories.findIndex(o => o.name === 'Brick Joint');
        if (copyIndex > -1) {
          copy.subCategories[copyIndex] = res;
        } else {
          copy.subCategories = [...copy.subCategories, res];
        }
        return copy;
      }
      return cat;
    } catch (_) {
      return cat;
    }
  };
  const fetchStoneColors = async (cat: ICategory) => {
    const stoneTypeOption = selectedOptions.filter(option => option.subCategory === 'Stone Type');
    const stoneTypeOptionId = stoneTypeOption[0]?.optionId;
    try {
      const res = await getStoneColorOptions(spec.buildId, {
        stoneTypeOptionId
      });
      // If Stone Color options, update/add to subCategories based on new fetch
      if (res.options.length) {
        const copy = JSON.parse(JSON.stringify(cat));
        const copyIndex = copy.subCategories.findIndex(o => o.name === 'Stone Color');
        if (copyIndex > -1) {
          copy.subCategories[copyIndex] = res;
        } else {
          copy.subCategories = [...copy.subCategories, res];
        }
        return copy;
      }
      return cat;
    } catch (_) {
      return cat;
    }
  };
  const fetchShutterColors = async (cat: ICategory) => {
    const exteriorColorSchemeOptions: ISelectedSpecOption[] = selectedOptions.filter(
      option => option.category === 'Exterior Color Scheme'
    );
    const exteriorColorSchemeCode = exteriorColorSchemeOptions.filter(
      option => option.subCategory === 'Exterior Color Scheme'
    )[0]?.name;
    try {
      const res = await getShutterColorOptions(spec.buildId, {
        exteriorColorSchemeCode,
        hasCedarUpgrade
      });
      // If options, update/add to subCategories based on new fetch
      if (res.options.length) {
        const copy = JSON.parse(JSON.stringify(cat));
        const copyIndex = copy.subCategories.findIndex(o => o.name === 'Shutter Color');
        if (copyIndex > -1) {
          copy.subCategories[copyIndex] = res;
        } else {
          copy.subCategories = [...copy.subCategories, res];
        }
        return copy;
      }
      return cat;
    } catch (_) {
      return cat;
    }
  };
  // used to save the spec as soon as you load to get up to date info
  const saveOneOffSpec = async (data: ISpecUpdate) => {
    try {
      await updateBuild(id, {
        selectedOptions: data.selectedOptions
          .map(option => {
            // Maps selectedOption to the correct return type
            return {
              optionId: option.optionId,
              category: option.category,
              subCategory: option.subCategory,
              price: option.price,
              isContractOption: option.isContractOption,
              quantity: option.quantity,
              optionIsLocked: option.optionIsLocked,
              optionSelectedAtContract: option.optionSelectedAtContract,
              name: option.name,
              pricingType: option.pricingType,
              imageUrl: option.imageUrl,
              enabled: option.enabled
            };
          })
          // remove duplicates
          .filter(
            (value, index, self) => index === self.findIndex(t => t.optionId === value.optionId)
          )
      });
      fetchDetails(false, false, true);
    } catch (e) {
      console.log(e);
    }
  };

  const [isSavingSpec, setIsSavingSpec] = useState(false);
  const saveSpec = async () => {
    setIsSavingSpec(true);
    const data = {
      selectedOptions: selectedOptions.map(option => {
        // Maps selectedOption to the correct return type
        return {
          enabled: option.enabled,
          optionId: option.optionId,
          category: option.category,
          subCategory: option.subCategory,
          price: option.price,
          isContractOption: option.isContractOption,
          quantity: option.quantity,
          optionIsLocked: option.optionIsLocked,
          optionSelectedAtContract: option.optionSelectedAtContract,
          name: option.name,
          pricingType: option.pricingType,
          imageUrl: option.imageUrl
        };
      })
    };
    try {
      await updateBuild(id, data);
      showSelectionDetails(false);
      fetchDetails(false);
      setSelectedCategoryIndex(0);
      enqueueSnackbar(`Spec saved successfully.`, {
        variant: 'success'
      });
    } catch (e) {
      console.log(e);
      enqueueSnackbar(`Error saving spec, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsSavingSpec(false);
    }
  };

  const saveSpecOnChange = async (currentSelectedOptions: ISelectedSpecOption[]) => {
    setIsSavingSpec(true);
    const data = {
      selectedOptions: currentSelectedOptions.map(option => {
        // Maps selectedOption to the correct return type
        return {
          enabled: option.enabled,
          optionId: option.optionId,
          category: option.category,
          subCategory: option.subCategory,
          price: option.price,
          isContractOption: option.isContractOption,
          quantity: option.quantity,
          optionIsLocked: option.optionIsLocked,
          optionSelectedAtContract: option.optionSelectedAtContract,
          name: option.name,
          pricingType: option.pricingType,
          imageUrl: option.imageUrl
        };
      })
    };
    try {
      await updateBuild(id, data);

      // If previously had a custom option and currently viewing Exterior Color Scheme, refresh the category
      const previousExteriorColorScheme = selectedOptions.filter(
        option => option.category === 'Exterior Color Scheme'
      );
      const hadCustomOption = previousExteriorColorScheme.some(option =>
        option.optionId.toLowerCase().includes('custom')
      );

      if (hadCustomOption && category?.name === 'Exterior Color Scheme') {
        const categories = contractCategories.concat(tenDayCategories);
        const selectedCategoryName = categories[selectedCategoryIndex];
        const isTenDay = selectedCategoryIndex >= contractCategories.length;

        const updatedCategory = await getBuildOptionCategory(id, {
          category: selectedCategoryName,
          isTenDayCategory: isTenDay
        });

        setCategory(updatedCategory);
      }

      fetchDetails(false, false, true);
    } catch (e) {
      console.log(e);
    } finally {
      setIsSavingSpec(false);
    }
  };
  const hasCedarUpgrade = selectedOptions.filter(option =>
    option.name.toLowerCase().includes('upgrade column/shutters - cedar')
  ).length
    ? true
    : false; // Specific to Shutter Color display
  const fetchSidingColors = async (cat: ICategory) => {
    const exteriorColorSchemeOptions: ISelectedSpecOption[] = selectedOptions.filter(
      option => option.category === 'Exterior Color Scheme'
    );
    const exteriorColorSchemeCode = exteriorColorSchemeOptions.filter(
      option => option.subCategory === 'Exterior Color Scheme'
    )[0]?.name;
    try {
      const res = await getSidingColorOptions(spec.buildId, {
        exteriorColorSchemeCode
      });
      // If options, update/add to subCategories based on new fetch
      if (res.options.length) {
        const copy = JSON.parse(JSON.stringify(cat));
        const copyIndex = copy.subCategories.findIndex(o => o.name === 'Siding Colors');
        if (copyIndex > -1) {
          copy.subCategories[copyIndex] = res;
        } else {
          copy.subCategories = [...copy.subCategories, res];
        }
        return copy;
      }
      return cat;
    } catch (_) {
      return cat;
    }
  };
  // When the selected category changes, update the list of build options
  useEffect(() => {
    let isCancelled = false;
    fetchCategory(undefined, true, isCancelled);

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIndex]);

  const toggleCategoriesDrawer = (isOpen: boolean) => (event: any) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setCategoriesDrawerOpen(isOpen);
  };

  const toggleBuildSummaryDrawer = (isOpen: boolean) => (event: any) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setBuildSummaryDrawerOpen(isOpen);
  };

  const isClient = user?.userRoles?.includes('BL_Client');

  const areContractOptionsComplete = useCallback(() => {
    if (
      (spec?.missingContractCategories?.length === 0 || spec?.missingContractCategories === null) &&
      !isClient
    ) {
      return true;
    }
    return false;
  }, [spec, isClient]);

  const [showLastContractOptionAlert, setShowLastContractOptionAlert] = useState(false);
  const { isLoading: isLoadingDoesContractExistData } = useQuery<{ hasContract: boolean }, Error>(
    ['does-contract-exist', id],
    async () => await doesContractExist(id),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !isPageLoading && areContractOptionsComplete(),
      onSuccess: data => {
        setShowLastContractOptionAlert(!data.hasContract);
      }
    }
  );

  const [showContractOptionsNotCompleteAlert, setShowContractOptionsNotCompleteAlert] =
    useState<boolean>(false);

  const hasBeenNotified = useRef(false);
  useMemo(() => {
    if (
      !hasBeenNotified.current &&
      !isPageLoading &&
      !areContractOptionsComplete() &&
      isTenDayCategorySelected
    ) {
      setShowContractOptionsNotCompleteAlert(true);
      hasBeenNotified.current = true;
    }
  }, [isPageLoading, areContractOptionsComplete, isTenDayCategorySelected]);

  const [showBuildVoidedAlert, setShowBuildVoidedAlert] = useState<boolean>(false);

  const hasBeenNotifiedOfVoidedBuild = useRef(false);
  useMemo(() => {
    if (!hasBeenNotifiedOfVoidedBuild.current && !isPageLoading && spec?.isBuildVoided) {
      setShowBuildVoidedAlert(true);
      hasBeenNotifiedOfVoidedBuild.current = true;
    }
  }, [isPageLoading, spec]);

  const buildStatusIsApproved = spec?.buildStatus === BuildStatus.Approved ? true : false;

  const contentRef = useContext(GlobalContext).contentRef;

  return (
    <Page
      title={pageTitle}
      showSideNav={!isHomeowner(user)}
      showSideBar={false}
      printOrientation="landscape"
      stickyContainer={selectsPageSlidingMenusFF}
      customBreadcrumbPath={redirect?.includes('contract-acceptance') ? redirect : undefined}
    >
      <>
        {isFetching && <Loader type="inline" position="centered" />}

        {!isFetching && !isShowingSelectionDetails && (
          <Box position="relative">
            <Grid container spacing={1}>
              {/* left nav */}
              {!isMobile && (
                /* desktop only */
                <Grid item xs={12} sm={2.6}>
                  <StickyBoxFeatureFlag offsetTop={0}>
                    <Box padding="1rem" border="1px solid #e1e1e1" borderRadius="4px">
                      {selectsPageSlidingMenusFF ? (
                        <AccordionListOptions
                          spec={spec}
                          contractCategories={contractCategories}
                          tenDayCategories={tenDayCategories}
                          selectedCategoryIndex={selectedCategoryIndex}
                          setSelectedCategoryIndex={setSelectedCategoryIndex}
                          setCategoriesDrawerOpen={setCategoriesDrawerOpen}
                          missingExteriorColorScheme={missingExteriorColorScheme}
                          isTenDayCategorySelected={isTenDayCategorySelected}
                        />
                      ) : (
                        <ListOptions
                          spec={spec}
                          contractCategories={contractCategories}
                          tenDayCategories={tenDayCategories}
                          selectedCategoryIndex={selectedCategoryIndex}
                          setSelectedCategoryIndex={setSelectedCategoryIndex}
                          setCategoriesDrawerOpen={setCategoriesDrawerOpen}
                          missingExteriorColorScheme={missingExteriorColorScheme}
                        />
                      )}
                    </Box>
                  </StickyBoxFeatureFlag>
                </Grid>
              )}

              {/* center nav */}
              <Grid item xs={12} md={7} className={classes.middleColumn}>
                {isTestBuild && (
                  <Box marginBottom="1rem">
                    <Alert icon={false} severity="error" className={classes.testbuildAlert}>
                      <Typography variant="h6" sx={{ margin: '0 auto' }}>
                        TEST BUILD
                      </Typography>
                    </Alert>
                  </Box>
                )}
                {isFetchingCategory && <Loader position="centered" />}
                {!isFetchingCategory && (
                  <>
                    <Box marginBottom="1rem">
                      <PageTitleWithBack
                        backText={getBackText()}
                        backUrl={getBackUrl()}
                        title={
                          isFetching || isFetchingCategory
                            ? ''
                            : category
                            ? `${isTenDayCategorySelected ? 'Ten Day' : 'Contract'} Options${
                                !!title ? ` - ${title}` : ''
                              }`
                            : 'Spec has no options, please select a different spec.'
                        }
                        subTitle={
                          (spec?.missingContractCategories?.length === 0 ||
                            spec?.missingContractCategories === null) &&
                          !spec.contractsGenerated && (
                            <Alert severity={ContractOptionsFinishedAlert.severity}>
                              {ContractOptionsFinishedAlert.message}
                            </Alert>
                          )
                        }
                        showDivider={false}
                      />
                      {isMobile && (
                        /* Display controls for sidebars in mobile only */
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Button
                            color="primary"
                            startIcon={<IconList style={{ fontSize: '28px' }} />}
                            onClick={toggleCategoriesDrawer(true)}
                            sx={() => ({
                              zIndex: 1
                            })}
                          />
                          <Drawer
                            anchor="left"
                            open={isCategoriesDrawerOpen}
                            onClose={toggleCategoriesDrawer(false)}
                            sx={{ zIndex: 1300 }}
                          >
                            <Box padding="1rem">
                              <ListOptions
                                spec={spec}
                                contractCategories={contractCategories}
                                tenDayCategories={tenDayCategories}
                                selectedCategoryIndex={selectedCategoryIndex}
                                setSelectedCategoryIndex={setSelectedCategoryIndex}
                                setCategoriesDrawerOpen={setCategoriesDrawerOpen}
                                missingExteriorColorScheme={missingExteriorColorScheme}
                              />
                            </Box>
                          </Drawer>
                          {!!spec && (
                            <>
                              <Button
                                color="primary"
                                startIcon={<Summarize style={{ fontSize: '28px' }} />}
                                onClick={toggleBuildSummaryDrawer(true)}
                              />
                              <Drawer
                                anchor="right"
                                open={isBuildSummaryDrawerOpen}
                                onClose={toggleBuildSummaryDrawer(false)}
                                sx={{ zIndex: 1300 }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="end"
                                  padding="1rem"
                                  paddingBottom={0}
                                >
                                  <Button
                                    startIcon={<Close />}
                                    color="primary"
                                    onClick={toggleBuildSummaryDrawer(false)}
                                  >
                                    Close
                                  </Button>
                                </Box>
                                <Box padding="1rem" width="17rem">
                                  <BuildSummary
                                    spec={spec}
                                    computedTotalPrice={spec.totalPrice}
                                    contractSummary={contractSummary}
                                    tenDaySummary={tenDaySummary}
                                    upgradeCap={spec.upgradeCap}
                                    showSelectionDetails={showSelectionDetails}
                                    lotPremium={spec.lotPremium}
                                    basePrice={spec.basePrice}
                                    path={path}
                                    upgradeCapOverage={spec.upgradeCapOverage}
                                    shouldShowFinishSpecButton={
                                      shouldShowFinishSpecButton && spec.isSpec && !spec?.clientId
                                    }
                                  />
                                </Box>
                              </Drawer>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/** CENTER CONTENT */}
                    <Box
                      padding="1rem"
                      border={!!category && '1px solid #e1e1e1'}
                      borderRadius="4px"
                    >
                      <Grid container spacing={2}>
                        {!!category?.note && (
                          <Grid item>
                            <Typography
                              dangerouslySetInnerHTML={{ __html: category?.note }}
                              variant="body1"
                              color="textSecondary"
                              className={classes.categoryNotes}
                            ></Typography>
                          </Grid>
                        )}
                        {/** LOADING INDICATOR */}
                        {category?.name === 'Color Scheme Options' ? (
                          <SpecColorSchemeOptions
                            selectedOptions={selectedOptions}
                            category={category}
                            setModalImageSrc={setModalImageSrc}
                            setSelectedOptions={data => {
                              saveSpecOnChange(data);
                              setSelectedOptions(data);
                            }}
                            fetchStoneColors={fetchStoneColors}
                            hasCedarUpgrade={hasCedarUpgrade}
                            spec={spec}
                            categoryIsLoading={stoneColorLoading}
                            disabled={buildStatusIsApproved || isFetchingDetails || isSavingSpec}
                          />
                        ) : (
                          <>
                            {category?.subCategories?.map(subCategory => {
                              // remove custom ones from available selected options
                              const selectedCategoryOptions = selectedOptions.filter(
                                o =>
                                  o.subCategory === subCategory.name &&
                                  o.isContractOption === subCategory.isContractOption
                              );
                              const currentSelectedOptionsInCategory =
                                selectedCategoryOptions.filter(
                                  o => !o.optionId.startsWith('CUSTOM')
                                );

                              const flatMissingSubcategories = (
                                subCategory.isContractOption
                                  ? [...(spec?.missingContractCategories ?? [])]
                                  : [...(spec?.missingTenDayCategories ?? [])]
                              )
                                ?.filter(cat => cat.category === category.name)
                                ?.flatMap(cat => cat.subCategories);
                              const isInvalidSubCategory =
                                flatMissingSubcategories?.filter(
                                  subCat => subCat === subCategory.name
                                )?.length > 0;

                              if (subCategory.name === 'Stone Color' && stoneColorLoading) {
                                return (
                                  <Grid item container flexDirection="column" marginBottom="1rem">
                                    <Stack key={'stone-color-loading'}>
                                      <Typography variant="body1" className={classes.bold}>
                                        {subCategory.name}
                                      </Typography>
                                      <Typography
                                        color="warning"
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Stone Color options are loading
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                );
                              }
                              if (
                                subCategory.name === 'Main Area Flooring Color' &&
                                mainAreaFlooringLoading
                              ) {
                                return (
                                  <Grid item container flexDirection="column" marginBottom="1rem">
                                    <Stack key={'flooring-options-loading'}>
                                      <Typography variant="body1" className={classes.bold}>
                                        {subCategory.name}
                                      </Typography>

                                      <Typography
                                        color="warning"
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Flooring Colors are loading
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                );
                              }
                              return (
                                <Grid item container key={subCategory.keyName}>
                                  {/** SUBCATEGORY NAME */}
                                  <Stack>
                                    <Typography variant="body1" className={classes.bold}>
                                      {subCategory.name}
                                    </Typography>
                                    {isInvalidSubCategory && (
                                      <Typography
                                        color="error"
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Missing required options
                                      </Typography>
                                    )}
                                    {!!subCategory.note && (
                                      <Typography variant="body2" color="textSecondary">
                                        {subCategory.note}
                                      </Typography>
                                    )}
                                  </Stack>
                                  {subCategory?.files && (
                                    <Grid item container flexDirection="column" marginBottom="1rem">
                                      {subCategory?.files?.map((file, index) => (
                                        <Typography
                                          variant="body1"
                                          key={`${index}`}
                                          color="textSecondary"
                                        >
                                          <Link href={file?.fileUrl} target="_blank">
                                            {file?.description}
                                          </Link>
                                        </Typography>
                                      ))}
                                    </Grid>
                                  )}
                                  {/** SUBCATEGORY OPTIONS */}
                                  {subCategory.options.map((option, subcategoryOptionIndex) => {
                                    const currentSelectedOption = selectedOptions.find(
                                      o => o.optionId === option.optionId
                                    );
                                    const currentSelectedIndex = selectedOptions.findIndex(
                                      o => o.optionId === option.optionId
                                    );
                                    return (
                                      <Fragment key={`${subcategoryOptionIndex}`}>
                                        <Grid
                                          item
                                          container
                                          key={option.optionId}
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          wrap="nowrap"
                                        >
                                          {!option.images && (
                                            <Grid item>
                                              {option.imageUrl ? (
                                                <picture
                                                  className={classes.picture}
                                                  onClick={() => setModalImageSrc(option.imageUrl)}
                                                >
                                                  <img
                                                    src={option.imageUrl}
                                                    alt={option.name}
                                                    className={classes.optionImage}
                                                  />
                                                </picture>
                                              ) : (
                                                <div className={classes.optionImage} />
                                              )}
                                            </Grid>
                                          )}
                                          <Grid item>
                                            <Radio
                                              checked={!!currentSelectedOption}
                                              disabled={
                                                buildStatusIsApproved ||
                                                !option.enabled ||
                                                isFetchingDetails ||
                                                isSavingSpec
                                              }
                                              onChange={() => {
                                                const copy = JSON.parse(
                                                  JSON.stringify(selectedOptions)
                                                );
                                                const correctSelectedIndex = copy.findIndex(
                                                  o =>
                                                    o.optionId ===
                                                    currentSelectedOptionsInCategory?.[0]?.optionId
                                                );
                                                if (
                                                  typeof correctSelectedIndex === 'number' &&
                                                  correctSelectedIndex > -1
                                                ) {
                                                  copy[correctSelectedIndex] = {
                                                    ...copy[correctSelectedIndex],
                                                    name: option.name,
                                                    optionId: option.optionId,
                                                    price: option.price,
                                                    quantity: 1,
                                                    imageUrl: option.imageUrl
                                                  };
                                                  saveSpecOnChange(copy);
                                                  setSelectedOptions(copy);
                                                } else {
                                                  saveSpecOnChange([...copy, option]);
                                                  setSelectedOptions([...copy, option]);
                                                }
                                              }}
                                              value={option.optionId}
                                              name={option.name}
                                              inputProps={{ 'aria-label': option.name }}
                                            />
                                          </Grid>
                                          {option.images && (
                                            <>
                                              <Grid
                                                item
                                                container
                                                spacing={1}
                                                className={`${classes.groupWrapper} ${classes.imagesWrapper}`}
                                                onClick={() => showSwatchModal(option.images)}
                                              >
                                                {/* group the images by `text` since there could be a collection of images like swatches that go together */}
                                                {Object.entries(
                                                  groupByKey(option.images, 'text')
                                                ).map(
                                                  (
                                                    group: [string, IOptionImage[]],
                                                    imageIndex: number
                                                  ) => {
                                                    return (
                                                      <Grid
                                                        item
                                                        key={`${imageIndex}`}
                                                        sx={{
                                                          marginTop: '1rem',
                                                          alignSelf: 'flex-end'
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="caption"
                                                          sx={{ display: 'block' }}
                                                        >
                                                          {/* the title of the image */}
                                                          {group[0]}
                                                        </Typography>
                                                        {group[1].length === 1 &&
                                                          group[1].map(
                                                            (
                                                              image: IOptionImage,
                                                              index: number
                                                            ) => {
                                                              return (
                                                                <div key={`${index}`}>
                                                                  <picture
                                                                    className={
                                                                      classes.pictureImages
                                                                    }
                                                                    onClick={() =>
                                                                      setModalImageSrc(
                                                                        image.imageUrl
                                                                      )
                                                                    }
                                                                  >
                                                                    {image.imageUrl && (
                                                                      <img
                                                                        className={
                                                                          classes.colorImage
                                                                        }
                                                                        src={image.imageUrl}
                                                                        alt={image.text}
                                                                      />
                                                                    )}
                                                                  </picture>
                                                                </div>
                                                              );
                                                            }
                                                          )}
                                                        {group[1].length > 1 && (
                                                          <div>
                                                            <picture
                                                              className={
                                                                classes.pictureSwatchImages
                                                              }
                                                              onClick={() =>
                                                                showSwatchModal(option.images)
                                                              }
                                                            >
                                                              {group[1].map(
                                                                (
                                                                  image: IOptionImage,
                                                                  index: number
                                                                ) => {
                                                                  return (
                                                                    <img
                                                                      key={`${index}`}
                                                                      style={{
                                                                        width: 50,
                                                                        height: 20
                                                                      }}
                                                                      src={image.imageUrl}
                                                                      alt={image.text}
                                                                    />
                                                                  );
                                                                }
                                                              )}
                                                            </picture>
                                                          </div>
                                                        )}
                                                      </Grid>
                                                    );
                                                  }
                                                )}
                                              </Grid>
                                            </>
                                          )}
                                          {!option.images && (
                                            <Grid item>
                                              <Typography>{option.name}</Typography>
                                            </Grid>
                                          )}
                                          {option?.files?.map(file => (
                                            <Grid item>
                                              <Link href={file?.fileUrl} target="_blank">
                                                {file?.description}
                                              </Link>
                                            </Grid>
                                          ))}
                                          <Grid
                                            item
                                            marginLeft="auto"
                                            className={isMobile ? classes.column : classes.row}
                                          >
                                            <Typography>{formatMoney(option.price)}</Typography>
                                            {!!currentSelectedOption &&
                                              option.price !== 0 &&
                                              !option.images &&
                                              option.enableQuantityInput && (
                                                <TextField
                                                  type="number"
                                                  label="Qty."
                                                  size="small"
                                                  error={!currentSelectedOption.quantity}
                                                  disabled={
                                                    buildStatusIsApproved ||
                                                    !option.enabled ||
                                                    isFetchingDetails ||
                                                    isSavingSpec
                                                  }
                                                  classes={{ root: classes.numberInput }}
                                                  value={currentSelectedOption.quantity}
                                                  InputLabelProps={{
                                                    shrink: !!currentSelectedOption.quantity
                                                  }}
                                                  onChange={event => {
                                                    const inputValue = event.currentTarget.value;
                                                    const value = Number.parseInt(inputValue, 10);

                                                    if (inputValue === '') {
                                                      // Allow field to be cleared
                                                      const copy = JSON.parse(
                                                        JSON.stringify(selectedOptions)
                                                      );
                                                      // Modifying an existing option
                                                      copy[currentSelectedIndex] = {
                                                        ...copy[currentSelectedIndex],
                                                        // Set the quantity to an empty string allowing the field to be cleared.
                                                        quantity: ''
                                                      };
                                                      setSelectedOptions(copy);
                                                    } else if (!Number.isNaN(value) && value >= 1) {
                                                      // Must order at least one. No negative numbers or zero.
                                                      const copy = JSON.parse(
                                                        JSON.stringify(selectedOptions)
                                                      );
                                                      // Modifying an existing option
                                                      copy[currentSelectedIndex] = {
                                                        ...copy[currentSelectedIndex],
                                                        quantity: value
                                                      };
                                                      setSelectedOptions(copy);
                                                    }
                                                  }}
                                                  onBlur={event => {
                                                    const inputValue = event.currentTarget.value;
                                                    const value = Number.parseInt(inputValue, 10);

                                                    if (
                                                      inputValue === '' ||
                                                      Number.isNaN(value) ||
                                                      value < 1
                                                    ) {
                                                      // Checks if input is empty, not a number, or less than 1 when input loses focus.
                                                      // Reset to 1 if field is empty or invalid
                                                      const copy = JSON.parse(
                                                        JSON.stringify(selectedOptions)
                                                      );
                                                      // Modifying an existing option
                                                      copy[currentSelectedIndex] = {
                                                        ...copy[currentSelectedIndex],
                                                        quantity: 1 // Resets to 1 to ensure there's always a valid minimum quantity.
                                                      };
                                                      setSelectedOptions(copy);
                                                      saveSpecOnChange(copy);
                                                    } else {
                                                      // Value is valid, so save the spec onChange as well
                                                      const copy = JSON.parse(
                                                        JSON.stringify(selectedOptions)
                                                      );
                                                      // Modifying an existing option
                                                      copy[currentSelectedIndex] = {
                                                        ...copy[currentSelectedIndex],
                                                        quantity: value
                                                      };
                                                      saveSpecOnChange(copy);
                                                    }
                                                  }}
                                                />
                                              )}
                                          </Grid>
                                        </Grid>
                                        {option.images && (
                                          <Grid item xs={12}>
                                            <Divider />
                                          </Grid>
                                        )}
                                      </Fragment>
                                    );
                                  })}
                                </Grid>
                              );
                            })}
                          </>
                        )}
                      </Grid>
                    </Box>
                    {category && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        marginTop="1rem"
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={selectedCategoryIndex === 0 || isSavingSpec}
                          onClick={() => {
                            setSelectedCategoryIndex(selectedCategoryIndex - 1);
                            if (selectsPageSlidingMenusFF) {
                              scrollToTopSticky(contentRef);
                            }
                          }}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="contained"
                          disabled={
                            (missingExteriorColorScheme &&
                              category?.name === 'Exterior Color Scheme') ||
                            isSavingSpec
                          }
                          onClick={() => {
                            if (category?.name === 'Color Scheme Options') {
                              showSelectionDetails(true);
                            } else {
                              if (selectsPageSlidingMenusFF) {
                                scrollToTopSticky(contentRef);
                              } else {
                                scrollToTop();
                              }

                              setSelectedCategoryIndex(selectedCategoryIndex + 1);
                            }
                          }}
                        >
                          {category?.name === 'Color Scheme Options' ? 'Done' : 'Next'}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
              {/* Right nav */}
              {!!spec && !isMobile && (
                /* desktop only */
                <Grid item xs={12} sm={2.4}>
                  <StickyBoxFeatureFlag>
                    <Box padding="1rem" border="1px solid #e1e1e1" borderRadius="4px">
                      <BuildSummary
                        spec={spec}
                        computedTotalPrice={spec.totalPrice}
                        contractSummary={contractSummary}
                        tenDaySummary={tenDaySummary}
                        upgradeCap={spec.upgradeCap}
                        showSelectionDetails={showSelectionDetails}
                        lotPremium={spec.lotPremium}
                        basePrice={spec.basePrice}
                        path={path}
                        upgradeCapOverage={spec.upgradeCapOverage}
                        shouldShowFinishSpecButton={
                          shouldShowFinishSpecButton && spec.isSpec && !spec?.clientId
                        }
                      />
                    </Box>
                  </StickyBoxFeatureFlag>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
        {!isClient && !spec?.isSpec && !isTestBuild && (
          <AlertModal
            open={showBuildVoidedAlert}
            setOpen={setShowBuildVoidedAlert}
            message={'This build has been voided.'}
            severity={'error'}
            title={'Build Voided'}
          />
        )}
        {!isClient && !spec?.isSpec && !spec?.isBuildVoided && !isTestBuild && (
          <AlertModal
            open={showContractOptionsNotCompleteAlert}
            setOpen={setShowContractOptionsNotCompleteAlert}
            message={'Please complete all Contract Options before moving to Ten Day Selections.'}
            severity={'error'}
            title={'Contract Options Incomplete'}
          />
        )}
        {!isLoadingDoesContractExistData &&
          showLastContractOptionAlert &&
          !spec?.isSpec &&
          !isClient &&
          !spec?.isBuildVoided &&
          !isTestBuild && (
            <AlertModal
              open={showLastContractOptionAlert}
              setOpen={setShowLastContractOptionAlert}
              message={ContractOptionsFinishedAlert.message}
              severity={ContractOptionsFinishedAlert.severity}
              title={'Contract Options Complete'}
            />
          )}
        {!!modalImageSrc && (
          <ImageModal
            isOpen={!!modalImageSrc}
            onClose={() => setModalImageSrc(null)}
            source={modalImageSrc}
          />
        )}
        {!!swatchImages && (
          <Modal
            open={!!swatchImages}
            onClose={() => {
              showSwatchModal(null);
            }}
            maxWidth="md"
          >
            <Fade in={!!swatchImages}>
              <Grid container className={classes.root}>
                {Object.entries(groupByKey(swatchImages, 'text')).map(
                  (group: [string, IOptionImage[]], imageIndex: number) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={`${imageIndex}`}
                        sx={{
                          marginTop: '1rem'
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {/* the title of the image */}
                          {group[0]}
                        </Typography>
                        {group[1].length === 1 &&
                          group[1].map((image: IOptionImage, index: number) => {
                            return (
                              <Grid item xs={12} sm={4} key={`${imageIndex}-${index}`}>
                                <div key={`${index}`}>
                                  {image?.imageUrl && (
                                    <picture>
                                      <img
                                        style={{
                                          width: 160,
                                          height: 160
                                        }}
                                        src={image?.imageUrl}
                                        alt={image.text}
                                      />
                                    </picture>
                                  )}
                                </div>
                              </Grid>
                            );
                          })}
                        {group[1].length > 1 && (
                          <Grid item xs={12} sm={4}>
                            <div>
                              <picture>
                                {group[1].map((image: IOptionImage, index: number) => {
                                  return (
                                    <img
                                      key={`${index}`}
                                      style={{
                                        width: 225,
                                        height: 60
                                      }}
                                      src={image.imageUrl}
                                      alt={image.text}
                                    />
                                  );
                                })}
                              </picture>
                            </div>
                          </Grid>
                        )}
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </Fade>
          </Modal>
        )}
        {isShowingSelectionDetails && (
          <SelectionDetails
            selectedOptions={selectedOptions}
            open={isShowingSelectionDetails}
            onClose={() => {
              fetchDetails(true);
              showSelectionDetails(false);
              window.scrollTo(0, 0);
            }}
            spec={spec}
            category={category}
            fetchCategories={fetchDetails}
            setHouseSaleContingencyLocation={setHouseSaleContingencyLocation}
            houseSaleContingencyLocation={houseSaleContingencyLocation}
            setHasHouseSaleContingency={setHasHouseSaleContingency}
            hasHouseSaleContingency={hasHouseSaleContingency}
            saveSpec={saveSpec}
            isSavingSpec={isSavingSpec}
            path={path}
            shouldShowFinishSpecButton={
              shouldShowFinishSpecButton && spec.isSpec && !spec?.clientId
            }
          />
        )}
      </>
    </Page>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  bold: {
    fontWeight: 600
  },
  optionImage: {
    cursor: 'pointer',
    height: 50,
    width: 50
  },
  picture: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& :not(:last-child)': {
      marginRight: theme.spacing(0.33)
    }
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    '& :not(:last-child)': {
      marginBottom: theme.spacing(0.33)
    }
  },
  numberInput: {
    width: '9ch',
    marginLeft: theme.spacing(1.5) + ' !important'
  },
  padLeft: {
    paddingLeft: theme.spacing(1)
  },
  colorImage: {
    width: 50,
    height: 50
  },
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  pictureSwatchImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      color: theme.palette.common.black,
      fontWeight: 'bold',
      fontSize: '.8rem',
      position: 'absolute',
      textAlign: 'center',
      padding: 8,
      '@media (min-width: 480px)': {
        width: 200,
        height: 30,
        padding: 0
      },

      '@media (min-width: 1340px)': {
        width: 50,
        height: 80,
        padding: 8
      },
      '@media (min-width: 1410px)': {
        width: 100,
        height: 50
      }
    }
  },
  imagesWrapper: {
    flexWrap: 'wrap',
    '@media (min-width: 1340px)': {
      flexWrap: 'nowrap'
    }
  },
  categoryNotes: {
    '& a': {
      color: theme.palette.primary.main
    }
  },
  middleColumn: {
    '@media (max-width: 1260px)': {
      flexGrow: 1,
      width: '100%',
      maxWidth: '100%'
    }
  },
  groupWrapper: {
    userSelect: 'none',
    cursor: 'pointer',
    position: 'relative',
    '&:hover::before': {
      color: theme.palette.common.black,
      fontWeight: 'bold',
      content: '"See Larger"',
      fontSize: '1rem',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -10%)',
      padding: 8,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      borderRadius: '10px',
      textAlign: 'center'
    }
  },
  testbuildAlert: {
    '& .MuiAlert-message': {
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 'auto',
      marginRight: 'auto'
    }
  }
}));
